import { __awaiter } from "tslib";
import * as React from 'react';
import { useRef, useState } from 'react';
import { Stack, Text, TextField } from '@fluentui/react';
/**
 * Manager typeahead (F8) — React port of the shared GRM "View other manager's team"
 * typeahead. Supports a local `managers` list or a remote `onSearch`; selecting a suggestion
 * fires `onSelect`. Self-dependent and testable without the FXP shell.
 */
export const ManagerTypeahead = ({ label = "View other manager\u2019s team", managers, onSearch, onSelect, minChars = 2, }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const latestRequest = useRef(0);
    const search = (q) => __awaiter(void 0, void 0, void 0, function* () {
        const term = q.toLowerCase();
        if (managers) {
            return managers.filter((m) => m.name.toLowerCase().includes(term) || m.alias.toLowerCase().includes(term));
        }
        if (onSearch) {
            return (yield onSearch(q)) || [];
        }
        return [];
    });
    const handleChange = (v) => __awaiter(void 0, void 0, void 0, function* () {
        setQuery(v);
        setSearchError(null);
        if (v.trim().length < minChars) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        const reqId = ++latestRequest.current;
        try {
            const results = yield search(v.trim());
            if (reqId !== latestRequest.current) {
                return;
            }
            setSuggestions(results);
            setOpen(true);
        }
        catch (_a) {
            if (reqId !== latestRequest.current) {
                return;
            }
            setSuggestions([]);
            setOpen(false);
            setSearchError('Unable to search managers. Please try again.');
        }
    });
    const pick = (m) => {
        onSelect(m);
        setQuery('');
        setSuggestions([]);
        setOpen(false);
    };
    return (React.createElement(Stack, { styles: { root: { position: 'relative', minWidth: 300 } }, "data-testid": "manager-typeahead" },
        React.createElement(TextField, { label: label, value: query, placeholder: "Search by name or alias", ariaLabel: label, onChange: (_e, v) => {
                void handleChange(v !== null && v !== void 0 ? v : '');
            } }),
        open && suggestions.length > 0 ? (React.createElement("div", { role: "listbox", "data-testid": "mt-suggestions", style: {
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 10,
                background: '#fff',
                border: '1px solid #edebe9',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            } }, suggestions.map((s) => (React.createElement("div", { key: s.alias, role: "option", "aria-selected": false, tabIndex: 0, "data-testid": `mt-option-${s.alias}`, style: { padding: '8px 12px', cursor: 'pointer' }, onClick: () => pick(s), onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    pick(s);
                }
            } },
            React.createElement(Text, null, s.name),
            ' ',
            React.createElement(Text, { variant: "small", styles: { root: { color: '#605e5c' } } },
                "(",
                s.alias,
                ")")))))) : null,
        open && suggestions.length === 0 && query.trim().length >= minChars ? (React.createElement("div", { "data-testid": "mt-empty", style: {
                position: 'absolute',
                top: '100%',
                padding: '8px 12px',
                background: '#fff',
                border: '1px solid #edebe9',
            } }, "No managers found.")) : null,
        searchError ? (React.createElement(Text, { role: "alert", "data-testid": "mt-error", styles: { root: { color: '#a4262c' } } }, searchError)) : null));
};
export default ManagerTypeahead;
//# sourceMappingURL=ManagerTypeahead.js.map